import React from "react";
import { FileAttachment } from "../types";
import { TaskDetailsInput, TaskShotPriority } from "@/__generated__/graphql";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, Formik, FieldArray, Field, FieldProps } from "formik";
import { InputField } from "@/components/common/InputField/InputField";
import { Service } from "../types";
import { getServiceColor } from "@/helpers/utils";
import { Info } from "lucide-react";
import { ChevronDown, PlusIcon, XIcon } from "lucide-react";
import { Dictionary, range } from "lodash";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toFormikValidationSchema } from "zod-formik-adapter";
import FileUploadField from "@/components/common/FileUpload/FileUpload";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface TaskDetailsInputData extends TaskDetailsInput {
  index: number;
}

const referenceFormSchema = z.object({
  fileAttachments: z.array(z.string()),
});

interface ListTasksProps {
  service: Service;
  projectId: string;
  onNext?: () => void;
  fileAttachments: FileAttachment[];
  updateFileAttachments: (fileAttachments: FileAttachment[]) => void;
  subServices: { id: string; name: string }[];
  updateTaskDetails: (tasks: TaskDetailsInputData[]) => void;
  submitting?: boolean;
  onSkipBidding?: () => void;
}

interface TasksFormValues {
  taskDetails: {
    name: string;
    serviceIds: string[];
  }[];
}

const tasksFormSchema = z.object({
  taskDetails: z
    .array(
      z.object({
        name: z.string().min(1, "Required"),
        serviceIds: z.array(z.string()),
      }),
    )
    .min(1, "At least one task is required"),
});

const ListTasks: React.FC<ListTasksProps> = ({
  service,
  projectId,
  onNext,
  fileAttachments,
  updateFileAttachments,
  subServices,
  updateTaskDetails,
  submitting,
  onSkipBidding,
}) => {
  const removeFileAttachments = (file: FileAttachment) => {
    const fileIndex = fileAttachments.findIndex((f) => f.id === file.id);
    if (fileIndex === -1) {
      return null;
    }
    return updateFileAttachments([
      ...fileAttachments.slice(0, fileIndex),
      { ...file, selected: false },
      ...fileAttachments.slice(fileIndex + 1),
    ]);
  };

  return (
    <Formik<TasksFormValues>
      initialValues={{
        taskDetails: [
          {
            name: "",
            serviceIds: [],
          },
        ],
      }}
      validateOnChange
      validationSchema={toFormikValidationSchema(tasksFormSchema)}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue, isValid }) => {
        return (
          <div className="flex flex-col !h-full overflow-scroll">
            <Form className="h-[calc(100%-172px)]">
              <div className="flex flex-col bg-primary gap-6 relative">
                <div className="flex w-full justify-between">
                  <div
                    className="w-1 h-full absolute"
                    style={{
                      background: getServiceColor(service.name),
                      borderRadius: "24px 0 0 24px",
                    }}
                  />
                  <div
                    className="ml-6 w-min h-min py-1 px-[20px] rounded-b-[12px] left-6 text-white text-nowrap"
                    style={{
                      background: getServiceColor(service.name),
                    }}
                  >
                    {service.name}
                  </div>
                </div>
                <div className="w-full m-5 mt-0 gap-4 flex gap-[24px] text-secondary-foreground text-base">
                  <div className="w-full flex flex-col gap-6">
                    <div className="flex gap-6">
                      <div className="w-[50%]">Task Name</div>
                      <div className="w-[50%]">Sub-service</div>
                    </div>
                    <div className="flex flex-col gap-6 text-sm">
                      <FieldArray name="taskDetails">
                        {({ remove, push }) => (
                          <>
                            {range(values.taskDetails.length).map(
                              (index: number) => {
                                const existingServiceIds =
                                  values.taskDetails[index] &&
                                  values.taskDetails[index].serviceIds
                                    ? values.taskDetails[index].serviceIds
                                    : [];
                                return (
                                  <div className="flex gap-6" key={index}>
                                    <div className="w-[50%] flex gap-2 items-center">
                                      <Field
                                        name={`taskDetails.${index}.name`}
                                        component={InputField}
                                        inputClassName="h-full border border-accent-foreground py-3 rounded-[8px] w-full cursor-pointer"
                                      />
                                      <Button
                                        className="border border-accent-foreground h-full"
                                        onClick={() => remove(index)}
                                      >
                                        <XIcon className="h-2 w-2" />
                                      </Button>
                                    </div>
                                    <div className="w-[50%] flex">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger className="w-full h-full">
                                          <div className="w-full h-full flex items-center gap-[8px]">
                                            {!!existingServiceIds.length && (
                                              <div
                                                className={cn(
                                                  "h-full w-full text-service-3d-tracking border border-service-3d-tracking",
                                                  "px-2 py-1 text-sm rounded-[8px] flex items-center justify-center",
                                                )}
                                              >
                                                {existingServiceIds.length}{" "}
                                                Services
                                                <ChevronDown />
                                              </div>
                                            )}
                                            {!existingServiceIds.length && (
                                              <Button className="h-full border border-accent-foreground w-full">
                                                <PlusIcon />
                                              </Button>
                                            )}
                                          </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                          className="p-4 flex flex-col gap-4"
                                          style={{ width: "100%" }}
                                          align="start"
                                        >
                                          {subServices.map((service) => (
                                            <Button
                                              key={service.id}
                                              className={cn(
                                                "h-full w-full text-service-3d-tracking border border-service-3d-tracking",
                                                "px-2 py-1 text-sm rounded-[8px] flex items-center justify-center",
                                              )}
                                              onClick={() => {
                                                if (
                                                  existingServiceIds.includes(
                                                    service.id,
                                                  )
                                                ) {
                                                  setFieldValue(
                                                    `taskDetails.${index}.serviceIds`,
                                                    existingServiceIds.filter(
                                                      (id) => id !== service.id,
                                                    ),
                                                  );
                                                } else {
                                                  setFieldValue(
                                                    `taskDetails.${index}.serviceIds`,
                                                    [
                                                      ...existingServiceIds,
                                                      service.id,
                                                    ],
                                                  );
                                                }
                                              }}
                                            >
                                              {service.name}
                                              {existingServiceIds.includes(
                                                service.id,
                                              ) && (
                                                <XIcon className="text-service-3d-tracking" />
                                              )}
                                            </Button>
                                          ))}
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>
                                );
                              },
                            )}
                            <div className="w-full">
                              <Button
                                className="bg-accent border border-accent-foreground"
                                onClick={() => push({})}
                              >
                                <PlusIcon />
                                Add Task
                              </Button>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                  <div className="min-w-[1px] min-h-full bg-accent-foreground" />
                  <div className="w-[35%] mr-10 flex flex-col gap-[20px]">
                    <div className="flex gap-2 items-bottom">
                      References
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="p-0 m-0 ml-[-4px]">
                            <Info className="h-4 w-4 mt-0.5" />
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="!p-2 !bg-primary border border-accent-foreground"
                          >
                            This area contains all non-shot assets we found in
                            the path specified. <br />
                            These files will be included in your bid package.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex flex-col gap-[12px] text-sm overflow-scroll">
                      {fileAttachments
                        .filter((f) => f.selected)
                        .map((file) => (
                          <div className="flex justify-between bg-tertiary rounded-[8px] px-3 py-2 items-center">
                            <div>{file.name}</div>
                            <Button
                              className="bg-transparent h-4 w-4"
                              onClick={() => removeFileAttachments(file)}
                            >
                              <XIcon />
                            </Button>
                          </div>
                        ))}
                      <Formik<{ fileAttachments: string[] }>
                        initialValues={{ fileAttachments: [] }}
                        validationSchema={referenceFormSchema}
                        onSubmit={() => {}}
                      >
                        <Form>
                          <Field
                            name="fileAttachments"
                            render={(props: FieldProps) => (
                              <FileUploadField
                                {...props}
                                projectId={projectId}
                                showFiles={false}
                                showLabel
                                onChange={(e: {
                                  target: {
                                    name: string;
                                    value: string[];
                                    keys: Dictionary<File>;
                                  };
                                }) => {
                                  const {
                                    target: { value, keys },
                                  } = e;
                                  const existingFileAttachmentIds =
                                    fileAttachments.map((f) => f.id);
                                  const newAttachments = value
                                    .filter(
                                      (id) =>
                                        existingFileAttachmentIds.indexOf(
                                          id,
                                        ) === -1,
                                    )
                                    .map((id) => ({
                                      id,
                                      name: keys[id].name,
                                      url: (keys[id] as File & { url?: string })
                                        .url,
                                      selected: true,
                                    }));
                                  updateFileAttachments([
                                    ...fileAttachments,
                                    ...newAttachments,
                                  ]);
                                }}
                              />
                            )}
                          />
                        </Form>
                      </Formik>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
            <div className="mt-3 bg-primary py-[16px] px-[24px] flex justify-end rounded-b-[24px] gap-3">
              {onSkipBidding && (
                <Button
                  className="text-service-paint p-3 rounded border !border-service-paint transition hover:opacity-80 disabled:opacity-50"
                  onClick={() => onSkipBidding()}
                >
                  Skip Bidding
                </Button>
              )}
              {onNext && (
                <Button
                  className="text-white py-[4px] px-[30px] rounded !border-white transition !bg-service-paint hover:opacity-80 disabled:opacity-50"
                  onClick={() => {
                    let index = 0;
                    const details: TaskDetailsInputData[] = [];
                    values.taskDetails.forEach((taskDetail) => {
                      if ((taskDetail.serviceIds || []).length === 0) {
                        index += 1;
                        details.push({
                          index,
                          name: taskDetail.name,
                          priority: TaskShotPriority.Medium,
                        });
                      } else {
                        taskDetail.serviceIds.forEach((serviceId) => {
                          index += 1;
                          details.push({
                            index,
                            name: taskDetail.name,
                            subServiceId: serviceId,
                            priority: TaskShotPriority.Medium,
                          });
                        });
                      }
                    });
                    updateTaskDetails(details);
                    onNext();
                  }}
                  disabled={submitting || !isValid}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default ListTasks;
